import { Injectable, Injector } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { EInvoiceValidationControl } from '@core';
import { ACLService } from '@delon/acl';
import { _HttpClient } from '@delon/theme';
import { Store } from '@ngrx/store';
import { finalize, Observable, pairwise, startWith, tap } from 'rxjs';
import { customerIsCreatedAction } from 'src/app/actions/customer/action';
import { DataBaseService } from 'src/app/shared-feature/services/_base/data-base.service';
import { AutoCodeService } from 'src/app/shared-feature/services/auto-code.service';
import { PostcodeLookupService } from 'src/app/shared-feature/services/postcode-lookup.service';

@Injectable()
export class CustomerService extends DataBaseService {
  // Define static properties at the top of the class
  static readonly exceededTypes = [
    { exceededType: 'W', name: 'Warning' },
    { exceededType: 'B', name: 'Block' }
  ];

  static readonly transactionTypes = [
    { transactionType: 'SINV', name: 'Sales Invoice' },
    { transactionType: 'CS', name: 'Cash Sale' },
    { transactionType: 'SDN', name: 'Sales Debit Note' },
    { transactionType: 'INV', name: 'Invoice' },
    { transactionType: 'DN', name: 'Customer Debit Notes' },
    { transactionType: 'SO', name: 'Sales Order' },
    { transactionType: 'DO', name: 'Delivery Order' }
  ];

  connecting = false;
  disconnecting = false;
  constructor(
    injector: Injector,
    private store?: Store
  ) {
    super(injector);
    this.aclKey = 'customer';
    this.nextRecordUrl = 'api/customers/NextRecordId';
    this.previousRecordUrl = 'api/customers/PreviousRecordId';
  }

  get autoCodeSrv() {
    return this.injector.get(AutoCodeService);
  }

  get acl() {
    return this.injector.get(ACLService);
  }

  get router() {
    return this.injector.get(ActivatedRoute);
  }

  get postcodeLookupSrv() {
    return this.injector.get(PostcodeLookupService);
  }

  form = this.createForm();

  get inUse() {
    return <boolean>this.form.controls.inUse.value;
  }

  get deliveryAddresses() {
    return this.form.controls.deliveryAddresses;
  }

  get taxExemptionItems() {
    return this.form.controls.taxExemptionItems;
  }

  get isInstantData() {
    const autoPopulate = this.router.snapshot.queryParams['autoPopulate'];
    return autoPopulate ?? false;
  }

  // Different with isEnabled from EInvoiceService
  // Customer maintenance allows partial or full E-Invoice validation
  get isEInvRequired(): boolean {
    // If the E-Invoice feature is not enabled, return false
    if (!this.eInvoiceService.isEnabled) return false;

    // Wait for the validation setting to be initialized
    // else use the default value from the E-Invoice service
    const validationControl = this.form.controls.validationControl.value;
    if (validationControl) {
      // Refer to EInvoiceValidationControl enum
      // If validation is mandatory, return true
      if (validationControl == EInvoiceValidationControl.FullCompliance)
        return true;

      // If validation is not required, return false
      if (
        validationControl == EInvoiceValidationControl.DeferredCompliance ||
        validationControl == EInvoiceValidationControl.NonCompliance
      )
        return false;
    }

    return this.eInvoiceService.requireCustomerValidation();
  }

  get showAlternatives() {
    if (!this.isEInvRequired) return false;

    const tinType = this.form.controls.tinType.value;
    if (tinType == 1 || tinType == 3 || tinType == 6) return false;

    return true;
  }

  get requiredField() {
    if (!this.isEInvRequired) return false;

    const tinType = this.form.controls.tinType.value;

    if (tinType && tinType >= 1 && tinType <= 6) {
      // Both fields required if ID Type is Malaysian Company or General Public
      if (tinType === 1 || tinType === 3) {
        return true;
      } else {
        const oneOfTheFieldsPopulated =
          this.form.controls.registrationNo.value ||
          this.form.controls.tinNumber.value;
        return !oneOfTheFieldsPopulated;
      }
    }

    return false;
  }

  // To display asterisk at billing tab
  get isBillingTabRequired() {
    if (!this.isEInvRequired) {
      return false;
    }

    return (
      // this.form.get('email')?.hasError('required') ||
      this.form.get('phoneNo1')?.hasError('required') ||
      this.form.get('address1')?.hasError('required') ||
      this.form.get('address2')?.hasError('required') ||
      this.form.get('state')?.hasError('required') ||
      this.form.get('country')?.hasError('required') ||
      // this.form.get('zipCode')?.hasError('required') ||
      this.form.get('city')?.hasError('required')
    );
  }

  // To display asterisk at E-Invoice tab
  get isEInvInfoTabRequired() {
    if (!this.isEInvRequired) {
      return false;
    }

    return this.form.get('tinNumber')?.hasError('required');
    // || this.form.get('salesTaxRegNo')?.hasError('required')
    // || this.form.get('serviceTaxRegNo')?.hasError('required')
  }

  clone() {
    if (this.form.controls.isDeferredTax.value && this.form.controls.enableDeferredVat.value === false){
      this.msg.error('Cannot clone Deferred VAT transaction while the Deferred VAT option is turned off. Please enable option to continue');
      return;
    }
    
    let obj = { ...this._data };
    obj.id = 0;
    obj.code = '';
    obj.active = true;
    obj.inUse = false;
    delete obj.legalEntityId;
    return obj;
  }

  new() {
    return this.http.get('api/customers/new');
  }

  get(id: string): Observable<any> {
    return this.http.get(`api/customers/${id}`);
  }

  create(body: any) {
    return this.http.post('api/customers/create/', body).pipe(
      tap(e => {
        if (this.store && this.isInstantData) {
          this.store.dispatch(customerIsCreatedAction({ newCustomer: e }));
        }
        return e;
      })
    );
  }

  update(body: any) {
    return this.http.post('api/customers/update/', body);
  }

  delete(id: string) {
    return new Promise((resolve, reject) => {
      this.http
        .delete(`api/customers`, null, { body: { ids: [id] } })
        .subscribe({
          next: res => resolve(res),
          error: () => reject()
        });
    });
  }

  private emailListValidator = (control: any) => {
    const emails = control.value;
    if (!emails || !Array.isArray(emails) || emails.length === 0) {
      return null;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(email => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      return { invalidEmail: { invalidEmails } };
    }

    const totalLength = emails.join(';').length;
    if (totalLength > 500) {
      return { maxLength: { actualLength: totalLength, requiredLength: 500 } };
    }

    return null;
  };

  override initParam(data: any) {
    // Handle all query parameters for auto-population
    const queryParams = this.router.snapshot.queryParams;
    
    if (this.id == 0) {
      // Customer Name
      if (queryParams['CustName']) {
        data.name = queryParams['CustName'];
      }
      
      // Customer Code
      if (queryParams['CustomerCode']) {
        data.code = queryParams['CustomerCode'];
      }
      
      // Control Account
      if (queryParams['ControlAccountId']) {
        data.controlAccountId = queryParams['ControlAccountId'];
      }
      
      // Currency
      if (queryParams['CurrencyId']) {
        data.currencyId = queryParams['CurrencyId'];
      }
      
      // Term
      if (queryParams['TermId']) {
        data.termId = queryParams['TermId'];
      }
      
      // Agent
      if (queryParams['AgentId']) {
        data.agentId = queryParams['AgentId'];
      }
      
      // Category
      if (queryParams['CategoryId']) {
        data.categoryId = queryParams['CategoryId'];
      }
      
      // Area
      if (queryParams['AreaId']) {
        data.areaId = queryParams['AreaId'];
      }
    }
    
    return data;
  }

  private loadControlAccountObject(data: any, controlAccountId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get(`api/accountCodes/${controlAccountId}`).subscribe({
        next: (response: any) => {
          data.controlAccount = response;
          resolve();
        },
        error: (error) => {
          console.error('Error loading control account:', error);
          reject(error);
        }
      });
    });
  }

  private loadCurrencyObject(data: any, currencyId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get(`api/currencies/${currencyId}`).subscribe({
        next: (response: any) => {
          data.currency = response;
          resolve();
        },
        error: (error) => {
          console.error('Error loading currency:', error);
          reject(error);
        }
      });
    });
  }

  private loadTermObject(data: any, termId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get(`api/terms/${termId}`).subscribe({
        next: (response: any) => {
          data.term = response;
          resolve();
        },
        error: (error) => {
          console.error('Error loading term:', error);
          reject(error);
        }
      });
    });
  }

  private loadAgentObject(data: any, agentId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get(`api/agents/${agentId}`).subscribe({
        next: (response: any) => {
          data.agent = response;
          resolve();
        },
        error: (error) => {
          console.error('Error loading agent:', error);
          reject(error);
        }
      });
    });
  }

  private loadCategoryObject(data: any, categoryId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get(`api/customercategories/${categoryId}`).subscribe({
        next: (response: any) => {
          data.category = response;
          resolve();
        },
        error: (error) => {
          console.error('Error loading category:', error);
          reject(error);
        }
      });
    });
  }

  private loadAreaObject(data: any, areaId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get(`api/areas/${areaId}`).subscribe({
        next: (response: any) => {
          data.area = response;
          resolve();
        },
        error: (error) => {
          console.error('Error loading area:', error);
          reject(error);
        }
      });
    });
  }

  initForm(data: any) {
    this.buildLines(
      this.form.controls.deliveryAddresses,
      () => this.createAddressForm(),
      data.deliveryAddresses?.length ?? 0
    );

    this.buildLines(
      this.form.controls.taxExemptionItems,
      () => this.createTaxExemptionItemForm(),
      data.taxExemptionItems?.length ?? 0
    );
    
    // Ensure creditLimitExceededBehavior defaults to 'B' (Block) if not set
    if (!data.creditLimitExceededBehavior) {
      data.creditLimitExceededBehavior = 'B';
    }
    
    this.amendValidators();
  }

  override setData(data: any) {
    // Call the parent setData method
    super.setData(data);
    
    // If this is a new customer (id == 0) and we have query parameters, load the objects asynchronously
    if (this.id == 0) {
      const queryParams = this.router.snapshot.queryParams;
      this.loadObjectsFromQueryParams(data, queryParams);
    }
  }

  private loadObjectsFromQueryParams(data: any, queryParams: any) {
    const promises: Array<Promise<void>> = [];

    // Control Account
    if (queryParams['ControlAccountId']) {
      promises.push(this.loadControlAccountObject(data, queryParams['ControlAccountId']));
    }
    
    // Currency
    if (queryParams['CurrencyId']) {
      promises.push(this.loadCurrencyObject(data, queryParams['CurrencyId']));
    }
    
    // Term
    if (queryParams['TermId']) {
      promises.push(this.loadTermObject(data, queryParams['TermId']));
    }
    
    // Agent
    if (queryParams['AgentId']) {
      promises.push(this.loadAgentObject(data, queryParams['AgentId']));
    }
    
    // Category
    if (queryParams['CategoryId']) {
      promises.push(this.loadCategoryObject(data, queryParams['CategoryId']));
    }
    
    // Area
    if (queryParams['AreaId']) {
      promises.push(this.loadAreaObject(data, queryParams['AreaId']));
    }

    // Wait for all objects to load, then update the form
    if (promises.length > 0) {
      Promise.all(promises).then(() => {
        // Add a small delay to ensure form controls are ready
        setTimeout(() => {
          // Update the form with the loaded objects
          this.form.patchValue({
            controlAccount: data.controlAccount,
            currency: data.currency,
            term: data.term,
            agent: data.agent,
            category: data.category,
            area: data.area
          });
          // Force change detection
          this.form.updateValueAndValidity();
        }, 100);
      });
    }
  }

  createForm() {
    const form = this.fb.group({
      id: ['', [Validators.maxLength(20)]],
      code: ['', [Validators.required, Validators.maxLength(50)]],
      name: ['', [Validators.required, Validators.maxLength(250)]],
      name2: ['', [Validators.maxLength(250)]],
      registrationNo: ['', [Validators.maxLength(20)]],
      status: [-1, []],
      country: ['', [Validators.maxLength(100)]],
      state: ['', [Validators.maxLength(100)]],
      city: ['', [Validators.maxLength(50)]],
      address1: ['', [Validators.maxLength(300)]],
      address2: ['', [Validators.maxLength(300)]],
      address3: ['', [Validators.maxLength(100)]],
      address4: ['', [Validators.maxLength(100)]],
      zipCode: ['', [Validators.maxLength(20)]],
      contactPerson: ['', [Validators.maxLength(200)]],
      email: ['', [Validators.maxLength(300)]],
      phoneNo1: ['', [Validators.maxLength(20)]],
      phoneNo2: ['', [Validators.maxLength(20)]],
      faxNo1: ['', [Validators.maxLength(20)]],
      faxNo2: ['', [Validators.maxLength(20)]],
      bizNature: ['', [Validators.maxLength(250)]],
      homePage: ['', [Validators.maxLength(100)]],
      notes: ['', [Validators.maxLength(500)]],
      tinBranchCode: ['', [Validators.maxLength(10)]],
      useBillingAsDOAddress: [false, []],
      controlAccountId: ['', []],
      controlAccount: [<any>null, [Validators.required]],
      areaId: [<any>null, []],
      area: [<any>null, []],
      termId: [<any>null, []],
      term: [<any>null, [Validators.required]],
      agentId: [<any>null, []],
      agent: [<any>null, []],
      currencyId: ['', [Validators.required]],
      currency: [<any>null, [Validators.required]],
      categoryId: [<any>null, []],
      category: [<any>null, []],
      birClassificationId: [<any>null, []],
      birClassification: [<any>null, []],
      defaultWVatCodeId: [<any>null, []],
      defaultWVatCode: [<any>null, []],
      isDeferredTax: [false, []],
      startDate: [<any>null, []],
      inUse: [false, []],
      validationControl: [0, []],
      tinType: [1, []],
      tinNumber: ['', [Validators.maxLength(14)]],
      salesTaxRegNo: ['', [Validators.maxLength(35)]],
      serviceTaxRegNo: ['', [Validators.maxLength(35)]],
      salesTaxExemptionNo: ['', [Validators.maxLength(100)]],
      salesTaxExemptionExpiryDate: [<any>null, []],
      frequencyOfBilling: ['', [Validators.maxLength(2)]],
      billingPeriodFrom: [new Date(), []],
      billingPeriodTo: [new Date(), []],
      isPeppolConnect: [false, []],
      peppolId: ['', []],
      taxExemptionItems: this.fb.array([this.createTaxExemptionItemForm()]),
      deliveryAddresses: this.fb.array([this.createAddressForm()]),
      creditLimitValue: [0.0],
      creditLimitValidUntil: [<any>null],
      creditLimitExceededBehavior: [
        CustomerService.exceededTypes
          .filter(e => e.exceededType === 'B')
          .map(e => e.exceededType)[0]
      ],
      creditLimitApplyForTransactions: [this.getFilteredTransactionTypes()],
      isCreditLimitActive: false,
      einvoiceEmail: ['', [Validators.maxLength(500)]],
      isAutoSendEStatement: [false, []],
      emailList: [[]],
      einvoiceEmailList: [[]],
      enableDeferredVat: [true, []]
    });
    form.controls.code.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      if (!val) {
        form.controls.code.setValue(`auto-${this.autoCodeSrv.activeFormat.id}`);
      }
    });
    form.controls.controlAccount.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.controlAccountId.setValue(val?.id);
    });
    form.controls.area.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.areaId.setValue(val?.id);
    });

    form.controls.term.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.termId.setValue(val?.id);
    });

    form.controls.agent.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.agentId.setValue(val?.id);
    });

    form.controls.currency.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.currencyId.setValue(val?.id);
    });
    form.controls.category.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.categoryId.setValue(val?.id);
    });
    form.controls.birClassification.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      if (val == null) {
        this.form.controls.defaultWVatCode.setValue(null);
      }

      this.form.controls.birClassificationId.setValue(val?.id);
      const birCat = val?.waCategory;
      if (birCat && birCat !== 2 && this.form.controls.defaultWVatCode) {
        this.form.controls.defaultWVatCode.setValue(null);
      }
    });
    form.controls.defaultWVatCode.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls.defaultWVatCodeId.setValue(val?.id);
    });

    form.controls.tinType.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;

      if (this.isEInvRequired) {
        // Refers to EINVOICE_TINTYPE (enum)
        const isPublic = val === 3;

        if (isPublic) {
          this.http.get('api/currencies/LocalLookup').subscribe(res => {
            let defaultValues = {
              name: 'General Public',
              tinNumber: 'EI00000000010',
              registrationNo: 'NA',
              salesTaxRegNo: 'NA',
              serviceTaxRegNo: 'NA',
              address1: 'NA',
              address2: 'NA',
              phoneNo1: 'NA',
              email: 'NA',
              currency: res,
              country: 'MY'
            };
            this.form.patchValue(defaultValues);
          });
        } else {
          // Refers to EINVOICE_TINTYPE (enum)
          const isMalaysian = val === 1 || val === 2;
          
          let defaultValues = {
            name:
              this.form.controls.name.value == 'General Public'
                ? null
                : this.form.controls.name.value,
            // if 6 (Government, set as 'EI00000000040')
            tinNumber:
              val === 6
                ? 'EI00000000040'
                : this.form.controls.tinNumber.value == 'EI00000000010' ||
                    this.form.controls.tinNumber.value == 'EI00000000020' ||
                    this.form.controls.tinNumber.value == 'EI00000000040'
                  ? null
                  : this.form.controls.tinNumber.value,
            // if 6 (Government, set as 'NA')
            registrationNo:
              val === 6
                ? 'NA'
                : this.form.controls.registrationNo.value == 'NA'
                  ? null
                  : this.form.controls.registrationNo.value,
            salesTaxRegNo:
              this.form.controls.salesTaxRegNo.value == 'NA'
                ? null
                : this.form.controls.salesTaxRegNo.value,
            serviceTaxRegNo:
              this.form.controls.serviceTaxRegNo.value == 'NCB'
                ? null
                : this.form.controls.serviceTaxRegNo.value,
            address1:
              this.form.controls.address1.value == 'NA'
                ? null
                : this.form.controls.address1.value,
            address2:
              this.form.controls.address2.value == 'NA'
                ? null
                : this.form.controls.address2.value,
            phoneNo1:
              this.form.controls.phoneNo1.value == 'NA'
                ? null
                : this.form.controls.phoneNo1.value,
            email:
              this.form.controls.email.value == 'NA'
                ? null
                : this.form.controls.email.value,
            country: this.getCountry(isMalaysian)
          };

          this.form.patchValue(defaultValues);
        }
      }

      switch (val) {
        case 3:
          this.form.controls.tinNumber.setValue('EI00000000010');
          break;
        case 4:
          this.form.controls.tinNumber.setValue('EI00000000020');
          this.form.controls.state.setValue('Not Applicable');
          break;
        case 5:
          this.form.controls.tinNumber.setValue('EI00000000020');
          this.form.controls.state.setValue('Not Applicable');
          break;
        case 6:
          this.form.controls.tinNumber.setValue('EI00000000040');
          break;
        default:
          this.form.controls.tinNumber.setValue('');
          break;
      }

      this.userDefaultService.setControlRequired(form.controls.agent!, 'Agent');

      this.updateRegNoAndTinVallidation();
      this.amendValidators();
    });

    form.controls.registrationNo.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.updateRegNoAndTinVallidation();
    });

    form.controls.tinNumber.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.updateRegNoAndTinVallidation();
    });

    form.controls.country.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;

      this.amendValidators();
      if (this.isEInvRequired) {     
        if(!form.controls.state.value && val !== 'MY')
            form.controls.state.setValue('Not Applicable');

        if((form.controls.state.value === 'Not Applicable' && val === 'MY') || !val)
          form.controls.state.setValue('');
      }
    });

    form.controls.validationControl.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.updateRegNoAndTinVallidation();
      this.amendValidators();
    });

    form.controls.emailList.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      const validEmails = Array.isArray(val)
        ? val.filter((e: any) => e && e.trim())
        : [];
      form.controls.email.setValue(validEmails.join(';'), { emitEvent: false });
    });

    form.controls.einvoiceEmailList.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      const validEmails = Array.isArray(val)
        ? val.filter((e: any) => e && e.trim())
        : [];
      form.controls.einvoiceEmail.setValue(validEmails.join(';'), {
        emitEvent: false
      });
    });

    // Postcode lookup for main form
    form.controls.zipCode.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.lookupAndFillAddress(val, form.controls.city, form.controls.state);
    });

    form.valueChanges.subscribe(() => {
      if (!this.observeChanges) return;
      this.modified = true;
    });
    this.userDefaultService.setControlRequired(form.get('category')!, 'CustomerCategory');
    this.userDefaultService.setControlRequired(form.get('area')!, 'Area');
    this.userDefaultService.setControlRequired(form.get('agent')!, 'agent');

    return form;
  }

  updateRegNoAndTinVallidation() {
    this.observeChanges = false;

    const regNo = this.form.controls.registrationNo;
    const tinNo = this.form.controls.tinNumber;

    // Default to -1 if null (might be PH company)
    const tinType = this.form.controls.tinType.value ?? -1;

    // Decide if user have E-Invoice requirement, if not, set to -1
    const processType =
      this.isEInvRequired && tinType >= 1 && tinType <= 6 ? tinType : -1;

    const oneOfTheFieldsPopulated = regNo.value || tinNo.value;

    var tinValidators = this.isEInvRequired
      ? [Validators.required, Validators.maxLength(14)]
      : [Validators.maxLength(14)];
    const regValidators = (length: number) => {
      return this.isEInvRequired
        ? [Validators.required, Validators.maxLength(length)]
        : [Validators.maxLength(length)];
    };

    switch (processType) {
      // Malaysian Company and Public General required both fields to be filled
      case 1:
        regNo.setValidators(regValidators(20)); //20
        tinNo.setValidators(tinValidators);
        break;

      case 3:
        regNo.setValidators(regValidators(12)); //12
        tinNo.setValidators(tinValidators);
        break;

      // Individual (Malaysia, Foreign) only needs to fill in one of the field
      case 2:
      case 4:
        regNo.setValidators(regValidators(12));
        tinNo.setValidators(tinValidators);

        if (oneOfTheFieldsPopulated) {
          regNo.removeValidators([Validators.required]);
          tinNo.removeValidators([Validators.required]);
        }
        break;

      case 5:
        regNo.setValidators(regValidators(13));
        tinNo.setValidators(tinValidators);

        if (oneOfTheFieldsPopulated) {
          regNo.removeValidators([Validators.required]);
          tinNo.removeValidators([Validators.required]);
        }
        break;
      case 6:
        regNo.setValidators(regValidators(20));
        tinNo.setValidators(tinValidators);

        if (oneOfTheFieldsPopulated) {
          regNo.removeValidators([Validators.required]);
          tinNo.removeValidators([Validators.required]);
        }
        break;
      default:
        regNo.setValidators([Validators.maxLength(20)]);
        tinNo.setValidators([Validators.maxLength(14)]);
        break;
    }

    regNo.updateValueAndValidity({ onlySelf: true });
    tinNo.updateValueAndValidity({ onlySelf: true });
    this.observeChanges = true;
  }

  createAddressForm() {
    const form = this.fb.group({
      branchCode: ['', [Validators.required, Validators.maxLength(20)]],
      branchName: ['', [Validators.required, Validators.maxLength(250)]],
      contactPerson: ['', [Validators.maxLength(200)]],
      email: ['', [Validators.maxLength(500)]],
      address1: ['', [Validators.maxLength(300)]],
      address2: ['', [Validators.maxLength(300)]],
      address3: ['', [Validators.maxLength(100)]],
      address4: ['', [Validators.maxLength(100)]],
      zipCode: ['', [Validators.maxLength(20)]],
      phoneNo: ['', [Validators.maxLength(100)]],
      faxNo: ['', [Validators.maxLength(100)]],
      registrationNo: ['', [Validators.maxLength(20)]],
      tinNumber: ['', [Validators.maxLength(14)]],
      tinType: [1, []],
      country: ['', [Validators.maxLength(100)]],
      state: ['', [Validators.maxLength(100)]],
      city: ['', [Validators.maxLength(50)]],
      isDefault: [false, []]
    });

    form.controls.country.valueChanges.pipe(
      startWith(form.controls.country.value),
      pairwise()
    ).subscribe(([oldVal, newVal]) => {
      // if (!this.observeChanges) return;

      if(oldVal != newVal)
        this.amendAddressFormValidators(form);

      if (this.isEInvRequired) {
        if(!form.controls.state.value && newVal !== 'MY')
          form.controls.state.setValue('Not Applicable');

        if((form.controls.state.value === 'Not Applicable' && newVal === 'MY') || !newVal)
          form.controls.state.setValue('');        
      }
    });

    form.controls.tinType.valueChanges.subscribe(val => {
      switch (val) {
        case 3:
          form.controls.tinNumber.setValue('EI00000000010');
          break;
        case 4:
          form.controls.state.setValue('Not Applicable');
          break;
        case 5:
          form.controls.tinNumber.setValue('EI00000000020');
          form.controls.state.setValue('Not Applicable');
          break;
        case 6:
          form.controls.tinNumber.setValue('EI00000000040');
          break;
        default:
          break;
      }
    });

    // Postcode lookup for delivery address form
    form.controls.zipCode.valueChanges.subscribe(val => {
      this.lookupAndFillAddress(val, form.controls.city, form.controls.state);
    });

    form.valueChanges.subscribe(() => {
      if (!this.observeChanges) return;
      this.modified = true;
    });
    return form;
  }

  createTaxExemptionItemForm() {
    const form = this.fb.group({
      taxExemptionNo: ['', [Validators.required, Validators.maxLength(20)]],
      taxExemptionDetails: ['', [Validators.maxLength(300)]],
      stockId: [<any>null, [Validators.required]],
      stock: [<any>null, [Validators.required]],
      taxCodeId: [<any>null, [Validators.required]],
      taxCode: [<any>null, [Validators.required]]
    });
    form.controls.stock.valueChanges.subscribe(val => {
      // if (!this.observeChanges) return;
      form.controls.stockId.setValue(val?.id);
    });
    form.controls.taxCode.valueChanges.subscribe(val => {
      // if (!this.observeChanges) return;
      form.controls.taxCodeId.setValue(val?.id);
    });

    form.valueChanges.subscribe(() => {
      // if (!this.observeChanges) return;
      this.modified = true;
    });
    return form;
  }

  get hideZipCodeField() {
    // for MY only
    return this.isEInvEnabled && this.form.controls.country.value !== 'MY';
  }

  // get zipRequired() {
  //   const isGeneralPublic = this.form.controls.tinType.value === 3;
  //   const isMalaysia = this.form.controls.country.value === 'MY';

  //   // If TIN type is 'General Public', zipCode and city is optional
  //   const required = this.isEInvRequired && isMalaysia && !isGeneralPublic;
  //   return required;
  // }

  get zoneRequired() {
    const isMalaysia = this.form.controls.country.value === 'MY';
    return this.isEInvRequired && isMalaysia;
  }

  private getCountry(isMalaysia: boolean) {
    // Return Malaysia if tinType is Malaysian related
    if (isMalaysia) return 'MY';

    // Reset country if tinType is Foreign and country is Malaysia
    if (!isMalaysia && this.form.controls.country.value === 'MY') return null;

    // Return existing value
    return this.form.controls.country.value;
  }

  public amendValidators() {
    this.observeChanges = false;
    const tinType = this.form.controls.tinType;

    // const emailListValidators = [];
    // if (this.isEInvRequired && tinType.value !== 3) {
    //   emailListValidators.push(Validators.required);
    // }
    // emailListValidators.push(this.emailListValidator.bind(this));

    // this.form.controls.emailList.setValidators(emailListValidators);
    // this.form.controls.emailList.updateValueAndValidity({ onlySelf: true });

    const phoneValidators = this.isEInvRequired
      ? [Validators.required, Validators.maxLength(20)]
      : [Validators.maxLength(20)];
    this.form.controls.phoneNo1.setValidators(phoneValidators);
    this.form.controls.phoneNo1.updateValueAndValidity({ onlySelf: true });

    const addressValidators = this.isEInvRequired
      ? [Validators.required, Validators.maxLength(300)]
      : [Validators.maxLength(300)];
    this.form.controls.address1.setValidators(addressValidators);
    this.form.controls.address1.updateValueAndValidity({ onlySelf: true });

    this.form.controls.address2.setValidators(addressValidators);
    this.form.controls.address2.updateValueAndValidity({ onlySelf: true });

    // const zipValidators = this.zipRequired
    //   ? [Validators.required, Validators.maxLength(5)]
    //   : [Validators.maxLength(20)];
    // this.form.controls.zipCode.setValidators(zipValidators);
    // this.form.controls.zipCode.updateValueAndValidity({ onlySelf: true });

    // const cityValidators = this.zipRequired
    //   ? [Validators.required, Validators.maxLength(50)]
    //   : [Validators.maxLength(50)];
    // this.form.controls.city.setValidators(cityValidators);
    // this.form.controls.city.updateValueAndValidity({ onlySelf: true });

    const stateValidators = this.zoneRequired
      ? [Validators.required, Validators.maxLength(100)]
      : [Validators.maxLength(100)];
    this.form.controls.state.setValidators(stateValidators);
    this.form.controls.state.updateValueAndValidity({ onlySelf: true });

    const countryValidators = this.isEInvRequired
      ? [Validators.required, Validators.maxLength(100)]
      : [Validators.maxLength(100)];
    this.form.controls.country.setValidators(countryValidators);
    this.form.controls.country.updateValueAndValidity({ onlySelf: true });

    /**
     * Disable controls that are not available for accountant plan
     */
    if (!this.acl.can('einv.full-validation')) {
      this.form.controls.validationControl.disable();
      this.form.controls.tinNumber.disable();
      this.form.controls.salesTaxRegNo.disable();
      this.form.controls.serviceTaxRegNo.disable();
      this.form.controls.einvoiceEmailList.disable();
    }

    /**
     * Address Form region
     */

    // this.form.controls.deliveryAddresses.controls.forEach(adForm => {
    //   const isRequired =
    //     this.isEInvRequired && adForm.controls.country.value === 'MY';

    //   const zipValidators = isRequired
    //     ? [Validators.required, Validators.maxLength(5)]
    //     : [Validators.maxLength(20)];
    //   adForm.controls.zipCode.setValidators(zipValidators);
    //   adForm.controls.zipCode.updateValueAndValidity({ onlySelf: true });

    //   const cityValidators = isRequired
    //     ? [Validators.required, Validators.maxLength(50)]
    //     : [Validators.maxLength(50)];
    //   adForm.controls.city.setValidators(cityValidators);
    //   adForm.controls.city.updateValueAndValidity({ onlySelf: true });

    //   const stateValidators = isRequired
    //     ? [Validators.required, Validators.maxLength(100)]
    //     : [Validators.maxLength(100)];
    //   adForm.controls.state.setValidators(stateValidators);
    //   adForm.controls.state.updateValueAndValidity({ onlySelf: true });
    // });
    this.observeChanges = true;
  }

  connectPeppol() {
    this.connecting = true;

    const body: any = {
      type: 'AR',
      id: this.id
    };

    this.http
      .post(`api/StorecovePeppol/ConnectPeppol`, body)
      .pipe(finalize(() => (this.connecting = false)))
      .subscribe(res => {
        this.form.controls.isPeppolConnect.setValue(res);
      });
  }

  disconnectPeppol() {
    this.disconnecting = true;

    const body: any = {
      type: 'AR',
      id: this.id
    };

    this.http
      .post(`api/StorecovePeppol/DisconnectPeppol`, body)
      .pipe(finalize(() => (this.disconnecting = false)))
      .subscribe(res => {
        this.form.controls.isPeppolConnect.setValue(res);
      });
  }

  // Credit Limit Control
  getFilteredTransactionTypes(): string[] {
    return CustomerService.transactionTypes
      .filter(t => t.transactionType !== 'SO' && t.transactionType !== 'DO')
      .map(t => t.transactionType);
  }

  // Add getters to maintain backward compatibility
  get exceededTypes() {
    return CustomerService.exceededTypes;
  }

  get transactionTypes() {
    return CustomerService.transactionTypes;
  }

  /**
   * Lookup postcode and auto-fill city and state if found
   * @param zipCode The postal code to lookup
   * @param cityControl The form control for city
   * @param stateControl The form control for state
   */
  private lookupAndFillAddress(
    zipCode: string | null,
    cityControl: any,
    stateControl: any
  ): void {
    // Only proceed if zipCode is 5 digits and country is Malaysia
    if (!zipCode || !/^\d{5}$/.test(zipCode)) {
      return;
    }

    this.postcodeLookupSrv.lookupPostcode(zipCode).subscribe(result => {
      if (result.found) {
        this.observeChanges = false; // Prevent triggering other valueChanges

        // Only update if current values are empty (don't replace existing values)
        if (!cityControl.value) {
          cityControl.setValue(result.city);
        }

        if (!stateControl.value) {
          stateControl.setValue(result.state);
        }

        this.observeChanges = true;
      } else {
      }
    });
  }

  amendAddressFormValidators(adForm: FormGroup, isCountryChanged: boolean = false) {
    if(this.isEInvEnabled)
    {
      const address1Validators = [Validators.required, Validators.maxLength(300)];
      adForm.controls['address1'].setValidators(address1Validators);
      adForm.controls['address1'].updateValueAndValidity({ onlySelf: true });

      const address2Validators = [Validators.required, Validators.maxLength(300)];
      adForm.controls['address2'].setValidators(address2Validators);
      adForm.controls['address2'].updateValueAndValidity({ onlySelf: true });
    }

    if(this.isEInvRequired) {
      const countryValidators = [Validators.required, Validators.maxLength(100)];
      adForm.controls['country'].setValidators(countryValidators);
      adForm.controls['country'].updateValueAndValidity({ onlySelf: true, emitEvent: isCountryChanged });

      const stateValidators = [Validators.required, Validators.maxLength(100)];
        adForm.controls['state'].setValidators(stateValidators);
        adForm.controls['state'].updateValueAndValidity({ onlySelf: true });

      const isRequired = adForm.controls['country'].value === 'MY';
      if(isRequired) {             
        const cityValidators = [Validators.required, Validators.maxLength(50)];
        adForm.controls['city'].setValidators(cityValidators);
        adForm.controls['city'].updateValueAndValidity({ onlySelf: true });

        // const zipValidators = [Validators.required, Validators.maxLength(20)];
        // adForm.controls['zipCode'].setValidators(zipValidators);
        // adForm.controls['zipCode'].updateValueAndValidity({ onlySelf: true });

        const registrationNoValidators = [Validators.required, Validators.maxLength(20)];
        adForm.controls['registrationNo'].setValidators(registrationNoValidators);
        adForm.controls['registrationNo'].updateValueAndValidity({ onlySelf: true });

        const tinNumberValidators = [Validators.required, Validators.maxLength(14)];
        adForm.controls['tinNumber'].setValidators(tinNumberValidators);
        adForm.controls['tinNumber'].updateValueAndValidity({ onlySelf: true });
      }
      else
      {
        const cityValidators = [Validators.maxLength(50)];
        adForm.controls['city'].setValidators(cityValidators);
        adForm.controls['city'].updateValueAndValidity({ onlySelf: true });

        const zipValidators = [Validators.maxLength(20)];
        adForm.controls['zipCode'].setValidators(zipValidators);
        adForm.controls['zipCode'].updateValueAndValidity({ onlySelf: true });

        const registrationNoValidators = [Validators.maxLength(20)];
        adForm.controls['registrationNo'].setValidators(registrationNoValidators);
        adForm.controls['registrationNo'].updateValueAndValidity({ onlySelf: true });

        const tinNumberValidators = [Validators.maxLength(14)];
        adForm.controls['tinNumber'].setValidators(tinNumberValidators);
        adForm.controls['tinNumber'].updateValueAndValidity({ onlySelf: true });
      }
    }
  }
}
